// client/src/hooks/usePageScrollRestoration.ts (ST-00087)
//
// Pages open at the top. The app never scrolls the window: the shell is a full-height flex box
// with `overflow: hidden`, and the page scrolls inside its content pane (the untabbed shell's
// `.page-pane`, or a tab's `.tab-pane`). That pane outlives the page in it: moving to another
// page swaps the pane's children, not the pane, so the new page inherited the old page's
// scroll offset. When the new page first rendered short (a spinner), the browser clamped the
// offset to 0 and it looked fine; when it rendered tall straight away (cached data, a big
// filter bar or skeleton) it opened part-way down. Hence "sometimes".
//
// React Router's <ScrollRestoration/> does not apply: it needs a data router, and the app runs
// a BrowserRouter (untabbed) or one MemoryRouter per tab; it also restores only the window's
// scroll. So the pane does it itself:
//   - a new navigation (PUSH / REPLACE) to another pathname starts at the top, or at the
//     element named by its #hash;
//   - Back / Forward (POP) returns to where that page was left, waiting for it to grow back
//     into that offset while its data loads (up to RESTORE_WINDOW_MS, and only until the user
//     scrolls on their own);
//   - a change of only the query string or the hash on the same page (filters, in-page tabs)
//     leaves the scroll alone, apart from moving to a new #hash.

import { useEffect, useLayoutEffect, useRef, type RefObject } from 'react';
import {
  useLocation,
  useNavigationType,
  type Location,
  type NavigationType,
} from 'react-router-dom';

/** How long Back / Forward keeps trying to reach the saved offset while the page loads. */
export const RESTORE_WINDOW_MS = 1500;

/** What the pane does after a navigation. */
export type PageScrollAction =
  | { kind: 'none' }
  | { kind: 'top' }
  | { kind: 'anchor'; id: string }
  | { kind: 'restore'; top: number };

type Place = Pick<Location, 'pathname' | 'hash'>;

/** The anchor id in a location's #hash ('' when there is none). */
function anchorOf(hash: string): string {
  if (!hash || hash === '#') return '';
  try {
    return decodeURIComponent(hash.slice(1));
  } catch {
    return hash.slice(1);
  }
}

/**
 * Decide what the pane does when the location moves from `prev` to `next`.
 *
 * @param prev   the location before; null on the pane's first render (a fresh pane is at the top)
 * @param next   the location now
 * @param type   how it got there: POP is Back / Forward, PUSH and REPLACE are new navigations
 * @param saved  the offset `next` was left at, when it was visited before in this pane
 */
export function pageScrollAction(
  prev: Place | null,
  next: Place,
  type: NavigationType | `${NavigationType}`,
  saved: number | undefined
): PageScrollAction {
  if (!prev) return { kind: 'none' };
  const samePage = prev.pathname === next.pathname;
  const anchor = anchorOf(next.hash);

  if (type === 'POP') {
    if (saved !== undefined) return { kind: 'restore', top: saved };
    // Nothing remembered (an entry from before the page loaded): treat it as a new page.
    if (samePage) return { kind: 'none' };
    return anchor ? { kind: 'anchor', id: anchor } : { kind: 'top' };
  }

  if (samePage) {
    // Filters and in-page tabs keep the scroll; only a new #anchor moves it.
    return anchor && next.hash !== prev.hash ? { kind: 'anchor', id: anchor } : { kind: 'none' };
  }
  return anchor ? { kind: 'anchor', id: anchor } : { kind: 'top' };
}

/** The element with id `id` inside `root`, by attribute so any id text is safe. */
function findAnchor(root: HTMLElement, id: string): HTMLElement | null {
  const safe = id.replace(/["\\]/g, '\\$&');
  return root.querySelector<HTMLElement>(`[id="${safe}"]`);
}

/** Input that means the user is scrolling on their own: a pending restore gives way to it. */
const USER_SCROLL_EVENTS = ['wheel', 'touchstart', 'pointerdown', 'keydown'] as const;

/**
 * Reset or restore the scroll of `paneRef` (the page's scroll container) on each navigation of
 * the enclosing router. Call it once per router, from a component inside that router.
 */
export default function usePageScrollRestoration(paneRef: RefObject<HTMLElement | null>): void {
  const location = useLocation();
  const navigationType = useNavigationType();
  // Offset each history entry was left at, by location.key (unique per entry).
  const savedRef = useRef(new Map<string, number>());
  const currentKeyRef = useRef(location.key);
  // The entry on show and the place before it. Kept by key, so StrictMode's second run of the
  // layout effect for the same entry repeats the same action instead of reading it as a no-op.
  const shownRef = useRef<{ key: string; place: Place } | null>(null);
  const fromRef = useRef<Place | null>(null);

  // Remember the offset as the user scrolls, so it is current when they leave. The scroll
  // events that follow a navigation's own reset land after currentKeyRef has moved on, so they
  // are filed under the new page and never overwrite the page left behind.
  useEffect(() => {
    const pane = paneRef.current;
    if (!pane) return undefined;
    const saved = savedRef.current;
    const onScroll = () => {
      saved.set(currentKeyRef.current, pane.scrollTop);
    };
    pane.addEventListener('scroll', onScroll, { passive: true });
    return () => pane.removeEventListener('scroll', onScroll);
  }, [paneRef]);

  // Layout effect: the pane moves before the browser paints the new page, so there is no
  // flash of the old offset.
  useLayoutEffect(() => {
    const pane = paneRef.current;
    if (shownRef.current?.key !== location.key) {
      fromRef.current = shownRef.current?.place ?? null;
      shownRef.current = {
        key: location.key,
        place: { pathname: location.pathname, hash: location.hash },
      };
    }
    currentKeyRef.current = location.key;
    if (!pane) return undefined;

    const action = pageScrollAction(
      fromRef.current,
      location,
      navigationType,
      savedRef.current.get(location.key)
    );
    if (action.kind === 'none') return undefined;

    // One step towards the target; true once it is reached.
    const step = (): boolean => {
      if (action.kind === 'top') {
        pane.scrollTop = 0;
        return true;
      }
      if (action.kind === 'anchor') {
        const el = findAnchor(pane, action.id);
        if (!el) return false;
        if (typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'start' });
        return true;
      }
      pane.scrollTop = action.top;
      return Math.abs(pane.scrollTop - action.top) <= 1;
    };

    // A new page with an anchor that has not rendered yet starts at the top meanwhile.
    if (action.kind === 'anchor') pane.scrollTop = 0;
    if (step()) return undefined;

    // The page is still loading: keep trying each frame until it has grown enough, the
    // window closes, the user scrolls, or the location moves on.
    let frame = 0;
    const deadline = performance.now() + RESTORE_WINDOW_MS;
    const stop = () => {
      if (frame) cancelAnimationFrame(frame);
      frame = 0;
      USER_SCROLL_EVENTS.forEach((type) => pane.removeEventListener(type, stop));
    };
    const tick = () => {
      frame = 0;
      if (step() || performance.now() >= deadline) {
        stop();
        return;
      }
      frame = requestAnimationFrame(tick);
    };
    USER_SCROLL_EVENTS.forEach((type) => pane.addEventListener(type, stop, { passive: true }));
    frame = requestAnimationFrame(tick);
    return stop;
    // location.key changes on every navigation, including to the same URL.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key, paneRef]);
}
